(() => {
  if (window.ggMobileExperience) return;
  window.ggMobileExperience = true;
  const navigationMedia = matchMedia('(max-width: 1149px)');
  const brandMedia = matchMedia('(max-width: 1100px)');
  const phoneMedia = matchMedia('(max-width: 749px)');
  const cleanups = new Map();

  function enhanceNavigation(drawer) {
    const panel = drawer.querySelector('.gg-mobile-panel');
    const head = drawer.querySelector('.gg-mobile-panel-head');
    const menu = drawer.querySelector('[data-gg-mobile-menu]');
    if (!panel || !head || !menu) return () => {};
    const listeners = new AbortController();
    const options = {signal: listeners.signal};
    const opener = drawer.closest('.shopify-section')?.querySelector('[data-gg-mobile-open]');
    const close = drawer.querySelector('button[data-gg-mobile-close]');
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'gg-mobile-back';
    back.textContent = '← Main menu';
    back.setAttribute('aria-label', 'Back to main menu');
    back.hidden = true;
    head.prepend(back);
    let active = null;
    let rootScroll = 0;
    function sync() {
      const next = menu.querySelector(':scope > details[open]');
      const previous = active;
      if (next && !active) rootScroll = menu.scrollTop;
      active = next;
      menu.classList.toggle('has-submenu', !!active);
      head.classList.toggle('has-submenu', !!active);
      back.hidden = !active;
      if (next !== previous) {
        menu.scrollTop = next ? 0 : rootScroll;
        if (drawer.classList.contains('is-open')) {
          (next ? back : previous?.querySelector('summary'))?.focus({preventScroll: true});
        }
      }
    }
    menu.querySelectorAll(':scope > details').forEach(item => item.addEventListener('toggle', sync, options));
    back.addEventListener('click', () => {
      menu.querySelectorAll(':scope > details[open]').forEach(item => { item.open = false; });
      sync();
    }, options);

    // Keep long category link groups in native, touch-friendly disclosures.
    const groups = [];
    menu.querySelectorAll('.gg-panel-group, .gg-catalog-group').forEach(group => {
      const title = group.querySelector('.gg-panel-group-title, .gg-catalog-group-title');
      if (!title) return;
      const original = [...group.childNodes];
      const details = document.createElement('details');
      details.className = 'gg-mobile-group';
      const summary = document.createElement('summary');
      summary.textContent = title.textContent;
      details.append(summary);
      original.filter(n => n !== title).forEach(n => details.append(n));
      group.replaceChildren(details);
      groups.push({group, original});
    });
    let wasOpen = drawer.classList.contains('is-open');
    function syncDialog() {
      const isOpen = drawer.classList.contains('is-open');
      opener?.setAttribute('aria-expanded', String(isOpen));
      if (isOpen && !wasOpen) close?.focus({preventScroll: true});
      if (!isOpen && wasOpen) opener?.focus({preventScroll: true});
      wasOpen = isOpen;
    }
    const observer = new MutationObserver(syncDialog);
    observer.observe(drawer, {attributes: true, attributeFilter: ['class']});
    drawer.addEventListener('keydown', event => {
      if (event.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      const focusable = [...drawer.querySelectorAll('a[href], button, summary, input, [tabindex="0"]')]
        .filter(el => !el.disabled && !el.closest('[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }, options);
    return () => {
      if (drawer.classList.contains('is-open')) close?.click();
      listeners.abort(); observer.disconnect(); back.remove();
      menu.classList.remove('has-submenu'); head.classList.remove('has-submenu');
      groups.forEach(({group, original}) => group.replaceChildren(...original));
      opener?.removeAttribute('aria-expanded');
    };
  }

  function enhanceBrand(main) {
    const listeners = new AbortController();
    const options = {signal: listeners.signal};
    const undo = [];
    // Keep the template's section order on every screen size.
    const jump = main.querySelector('.gb-jump');
    const jumpLinks = [...(jump?.querySelectorAll('a[href^="#"]') || [])];
    if (jump && jumpLinks.length) {
      const label = document.createElement('label');
      label.className = 'gb-mobile-jump gb-wrap';
      label.id = 'brand-page-menu';
      const text = document.createElement('span');
      text.textContent = 'On this page';
      const select = document.createElement('select');
      select.setAttribute('aria-label', 'Jump to a brand page section');
      select.add(new Option('Choose a section', ''));
      jumpLinks.forEach(link => select.add(new Option(link.textContent.trim(), link.hash)));
      if (main.querySelector('#brand-help')) select.add(new Option('Get expert help', '#brand-help'));
      label.append(text, select); jump.append(label); jump.classList.add('gb-has-mobile-jump');
      select.addEventListener('change', () => {
        const target = document.getElementById(select.value.slice(1));
        if (!target) return;
        location.hash = select.value;
        target.scrollIntoView({block: 'start', behavior: 'instant'});
        const heading = target.querySelector('h2');
        if (heading) {
          const oldTabindex = heading.getAttribute('tabindex');
          heading.tabIndex = -1; heading.focus({preventScroll: true});
          const restoreTabindex = () => {
            if (oldTabindex === null) heading.removeAttribute('tabindex');
            else heading.setAttribute('tabindex', oldTabindex);
          };
          heading.addEventListener('blur', restoreTabindex, {...options, once: true});
          undo.push(restoreTabindex);
        }
        select.value = '';
      }, options);
      undo.push(() => { label.remove(); jump.classList.remove('gb-has-mobile-jump'); });
    }
    // Keep the category chooser next to the results, without scrolling past it.
    main.querySelectorAll('gg-brand-catalog').forEach(catalog => {
      const controls = [...catalog.querySelectorAll('[data-category-control]')];
      const categories = catalog.querySelector('.gb-categories');
      if (!controls.length || !categories) return;
      const label = document.createElement('label');
      label.className = 'gb-mobile-category-picker';
      const text = document.createElement('span'); text.textContent = 'Category';
      const select = document.createElement('select');
      controls.forEach(control => select.add(new Option(control.querySelector('.gb-category-label').textContent.trim(), control.dataset.categoryControl)));
      select.setAttribute('aria-label', 'Shop by category');
      label.append(text, select); categories.before(label); categories.classList.add('gb-mobile-categories-source');
      const sync = () => {
        const active = controls.find(control => control.getAttribute('aria-expanded') === 'true');
        select.value = active?.dataset.categoryControl || '';
      };
      sync();
      const observer = new MutationObserver(sync);
      controls.forEach(control => observer.observe(control, {attributes: true, attributeFilter: ['aria-expanded']}));
      select.addEventListener('change', () => {
        const control = controls.find(control => control.dataset.categoryControl === select.value);
        if (control?.getAttribute('aria-expanded') !== 'true') control?.click();
      }, options);
      undo.push(() => { observer.disconnect(); label.remove(); categories.classList.remove('gb-mobile-categories-source'); });
    });
    const prose = main.querySelector('.gb-about-copy > .gb-prose');
    if (prose && prose.children.length > 1) {
      const original = [...prose.childNodes];
      const story = document.createElement('div');
      story.className = 'gb-mobile-story';
      const content = document.createElement('div');
      content.id = 'gb-brand-story-content';
      content.hidden = true;
      [...prose.children].slice(1).forEach(n => content.append(n));
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'gb-story-toggle';
      button.setAttribute('aria-controls', content.id);
      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      button.append(arrow);
      const render = () => {
        button.setAttribute('aria-expanded', String(!content.hidden));
        button.setAttribute('aria-label', content.hidden ? 'Expand brand story' : 'Collapse brand story');
        arrow.textContent = content.hidden ? '↓' : '↑';
      };
      render();
      button.addEventListener('click', () => {
        content.hidden = !content.hidden;
        render();
        // The control follows the text in both states and remains reachable after collapse.
        if (content.hidden && button.getBoundingClientRect().top < 140) {
          button.scrollIntoView({block: 'center', behavior: 'instant'});
        }
      }, options);
      story.append(content, button); prose.append(story);
      undo.push(() => prose.replaceChildren(...original));
    }
    main.querySelectorAll('.gb-life-item').forEach(article => {
      const original = [...article.childNodes];
      const photo = article.querySelector('.gb-life-image');
      const heading = article.querySelector('h3');
      if (!photo || !heading) return;
      const details = document.createElement('details');
      details.className = 'gb-mobile-application';
      const summary = document.createElement('summary');
      const content = document.createElement('div');
      summary.append(photo, heading);
      original.filter(n => n !== photo && n !== heading).forEach(n => content.append(n));
      details.append(summary, content); article.replaceChildren(details);
      undo.push(() => article.replaceChildren(...original));
    });
    // Use smaller batches on touch screens without losing any products or desktop state.
    const grids = [];
    main.querySelectorAll('.gb-grid').forEach((grid, index) => {
      const cards = [...grid.children];
      if (cards.length <= 4) return;
      const originalHidden = cards.map(card => card.hidden);
      const nativeButton = grid.parentElement.querySelector('[data-show-more]');
      nativeButton?.classList.add('gb-native-more');
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'gb-button'; button.dataset.mobileMore = '';
      const oldId = grid.getAttribute('id');
      if (!grid.id) grid.id = 'gb-mobile-products-' + index;
      button.setAttribute('aria-controls', grid.id);
      grid.after(button); grid.classList.add('gb-mobile-paged');
      let visible = phoneMedia.matches ? 4 : 6;
      let expanded = false;
      const render = () => {
        cards.forEach((card, i) => { card.hidden = i >= visible; });
        button.hidden = visible >= cards.length;
        button.textContent = `Show ${Math.min(phoneMedia.matches ? 4 : 6, Math.max(0, cards.length - visible))} more (${Math.max(0, cards.length - visible)} remaining)`;
      };
      render();
      button.addEventListener('click', () => {
        const firstNew = cards[visible]?.querySelector('a');
        expanded = true;
        visible += phoneMedia.matches ? 4 : 6;
        render(); firstNew?.focus({preventScroll: true});
      }, options);
      phoneMedia.addEventListener('change', () => {
        if (!expanded) visible = phoneMedia.matches ? 4 : 6;
        render();
      }, options);
      grids.push({grid, cards, originalHidden, nativeButton, button, oldId});
    });
    return () => {
      listeners.abort();
      grids.forEach(({grid, cards, originalHidden, nativeButton, button, oldId}) => {
        cards.forEach((card, i) => { card.hidden = originalHidden[i]; });
        nativeButton?.classList.remove('gb-native-more'); button.remove(); grid.classList.remove('gb-mobile-paged');
        if (oldId === null) grid.removeAttribute('id');
      });
      undo.reverse().forEach(fn => fn());
    };
  }

  function reconcile() {
    for (const [node, record] of cleanups) {
      if (!node.isConnected || !record.media.matches) { record.cleanup(); cleanups.delete(node); }
    }
    if (navigationMedia.matches) document.querySelectorAll('[data-gg-mobile-drawer]').forEach(node => {
      if (!cleanups.has(node)) cleanups.set(node, {media: navigationMedia, cleanup: enhanceNavigation(node)});
    });
    const main = document.querySelector('#MainContent');
    if (brandMedia.matches && main?.querySelector('.gb-hero') && !cleanups.has(main)) {
      cleanups.set(main, {media: brandMedia, cleanup: enhanceBrand(main)});
    }
  }
  navigationMedia.addEventListener('change', reconcile);
  brandMedia.addEventListener('change', reconcile);
  document.addEventListener('shopify:section:load', () => {
    const main = document.querySelector('#MainContent');
    const record = cleanups.get(main);
    if (record) { record.cleanup(); cleanups.delete(main); }
    reconcile();
  });
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(reconcile));
  if (document.readyState !== 'complete') document.addEventListener('DOMContentLoaded', reconcile, {once: true});
  else reconcile();
})();
